/**
 * Global Search Hooks
 * React Query hooks for unified full-text search across all FMEA entities
 */

import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../api/config'

const API_BASE = `${API_BASE_URL}/global-search`

/**
 * Search result type interfaces
 */
export interface ComponentSearchResult {
  id: string
  name: string
  type: string
  description: string | null
  path: string[]
  matchType: 'name' | 'type' | 'description'
  score: number
  verificationStatus: string
}

export interface FailureModeSearchResult {
  id: string
  description: string
  category: string
  rpn: number | null
  riskLevel: string
  severity: number | null
  occurrence: number | null
  detection: number | null
  components: Array<{
    id: string
    name: string
  }>
  score: number
}

export interface RootCauseSearchResult {
  id: string
  description: string
  category: string
  contributingFactors: string | null
  linkedFailureModes: Array<{
    id: string
    description: string
    rpn: number | null
  }>
  score: number
}

export interface MitigationSearchResult {
  id: string
  description: string
  status: string
  rpnBefore: number
  rpnAfter: number | null
  failureMode: {
    id: string
    description: string
    currentRPN: number | null
  } | null
  score: number
}

export interface RequirementSearchResult {
  id: string
  requirementId: string
  title: string
  description: string
  type: string
  priority: string
  verificationStatus: string
  matchType: 'requirementId' | 'title' | 'description'
  score: number
}

export interface GlobalSearchResults {
  components: ComponentSearchResult[]
  failureModes: FailureModeSearchResult[]
  rootCauses: RootCauseSearchResult[]
  mitigations: MitigationSearchResult[]
  requirements: RequirementSearchResult[]
  metadata: {
    totalResults: number
    queryTime: number
    query: string
  }
}

export interface SearchStats {
  totalComponents: number
  totalFailureModes: number
  totalRootCauses: number
  totalMitigations: number
  totalRequirements: number
  totalEntities: number
}

/**
 * Search across all entity types
 */
export const useGlobalSearch = (
  query: string,
  options?: {
    limit?: number
    offset?: number
    entityTypes?: string[]
    projectId?: string
    enabled?: boolean
  }
) => {
  const { limit = 50, offset = 0, entityTypes, projectId, enabled = true } = options || {}

  return useQuery<GlobalSearchResults>({
    queryKey: ['global-search', query, limit, offset, entityTypes, projectId],
    queryFn: async () => {
      const params = new URLSearchParams({
        q: query,
        limit: String(limit),
        offset: String(offset),
      })

      if (entityTypes && entityTypes.length > 0) {
        params.append('types', entityTypes.join(','))
      }

      if (projectId) {
        params.append('projectId', projectId)
      }

      const response = await fetch(`${API_BASE}?${params}`)
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Search failed')
      }
      const data = await response.json()
      return data.data
    },
    enabled: enabled && query.trim().length >= 2,
    staleTime: 30000,
    placeholderData: (prev) => prev,
  })
}

/**
 * Search components only
 */
export const useSearchComponents = (query: string, limit: number = 50) => {
  return useQuery<ComponentSearchResult[]>({
    queryKey: ['search-components', query, limit],
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE}/components?q=${encodeURIComponent(query)}&limit=${limit}`
      )
      if (!response.ok) {
        throw new Error('Component search failed')
      }
      const data = await response.json()
      return data.data
    },
    enabled: query.trim().length >= 2,
    staleTime: 30000,
  })
}

/**
 * Search failure modes only
 */
export const useSearchFailureModes = (query: string, limit: number = 50) => {
  return useQuery<FailureModeSearchResult[]>({
    queryKey: ['search-failure-modes', query, limit],
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE}/failure-modes?q=${encodeURIComponent(query)}&limit=${limit}`
      )
      if (!response.ok) {
        throw new Error('Failure mode search failed')
      }
      const data = await response.json()
      return data.data
    },
    enabled: query.trim().length >= 2,
    staleTime: 30000,
  })
}

/**
 * Search root causes only
 */
export const useSearchRootCauses = (query: string, limit: number = 50) => {
  return useQuery<RootCauseSearchResult[]>({
    queryKey: ['search-root-causes', query, limit],
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE}/root-causes?q=${encodeURIComponent(query)}&limit=${limit}`
      )
      if (!response.ok) {
        throw new Error('Root cause search failed')
      }
      const data = await response.json()
      return data.data
    },
    enabled: query.trim().length >= 2,
    staleTime: 30000,
  })
}

/**
 * Search mitigations only
 */
export const useSearchMitigations = (query: string, limit: number = 50) => {
  return useQuery<MitigationSearchResult[]>({
    queryKey: ['search-mitigations', query, limit],
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE}/mitigations?q=${encodeURIComponent(query)}&limit=${limit}`
      )
      if (!response.ok) {
        throw new Error('Mitigation search failed')
      }
      const data = await response.json()
      return data.data
    },
    enabled: query.trim().length >= 2,
    staleTime: 30000,
  })
}

/**
 * Get search statistics
 */
export const useSearchStats = () => {
  return useQuery<SearchStats>({
    queryKey: ['search-stats'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/stats`)
      if (!response.ok) {
        throw new Error('Failed to fetch search stats')
      }
      const data = await response.json()
      return data.data
    },
    staleTime: 60000,
  })
}
